import { useCallback, useEffect, useRef, useState } from 'react';

import { CBCC_EVENT, trackCbccEvent } from '../services/analytics';
import { showCbccErrorToast } from '../services/cbcc-toast';
import { handleBankRedirection } from '../services/redirect-to-bank';
import { submitOnboard } from '../services/submit-onboard';
import type { OnboardResponse } from '../types';
import { useResetOnReturn } from './use-reset-on-return';

/**
 * Bank onboarding handoff for already-eligible users (CVP
 * `userFlow === "ONBOARDING"`). On success, form-POSTs to the bank. Call
 * `startOnboarding` on the bottom-CTA click for that flow.
 */
export function useCbccOnboard() {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<unknown>(null);

	const isMounted = useRef(true);
	useEffect(() => {
		// Re-arm on every effect run — StrictMode runs mount → cleanup → mount,
		// so without this the ref stays false and state updates get dropped.
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	// `isLoading` intentionally stays true through the bank navigation; release it
	// when the user comes Back (the handoff is a cross-origin form POST, so this
	// is a hard reload, not a bfcache restore — see useResetOnReturn).
	useResetOnReturn(() => setIsLoading(false));

	const startOnboarding =
		useCallback(async (): Promise<OnboardResponse | null> => {
			setIsLoading(true);
			setError(null);
			try {
				const response = await submitOnboard();
				const redirected = handleBankRedirection(response);
				if (redirected) {
					// Fired only when a bank redirect actually fires (redirect URL
					// returned), just before the page navigates away.
					trackCbccEvent(CBCC_EVENT.REDIRECTED_TO_BANK);
					// GTM dataLayer push, paired with the event above (fintech-ui #11).
					// Guarded — fin-apps has no GTM wired yet; this no-ops until a
					// dataLayer exists, then starts working with no further change.
					if (typeof window !== 'undefined') {
						(
							window as typeof window & { dataLayer?: unknown[] }
						).dataLayer?.push({ event: 'cbccApplyBtnClicked' });
					}
				}
				if (!redirected && isMounted.current) setIsLoading(false);
				return response;
			} catch (err) {
				showCbccErrorToast();
				if (isMounted.current) {
					setError(err);
					setIsLoading(false);
				}
				return null;
			}
		}, []);

	return { startOnboarding, isLoading, error };
}
